import React from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';

const fmt = (n) => '\u20B9 ' + n.toLocaleString('en-IN');

const CartPage = () => {
  const { items, updateQty, removeFromCart, total, clearCart } = useCart();

  if (items.length === 0) {
    return (
      <main className="bg-[#FAF7F2] min-h-[60vh]">
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 rounded-full bg-[#E8B530]/15 flex items-center justify-center mx-auto mb-5">
            <ShoppingBag className="text-[#1F2A44]" size={28} />
          </div>
          <h1 className="font-serif text-3xl font-bold text-[#1F2A44]">Your cart is empty</h1>
          <p className="text-[#1F2A44]/70 mt-2">Discover beautiful furniture for your home.</p>
          <Link to="/shop" className="inline-block mt-6 bg-[#E8B530] hover:bg-[#D4A220] text-[#1F2A44] font-semibold px-7 py-3 uppercase tracking-wider text-sm transition-colors">Start Shopping</Link>
        </div>
      </main>
    );
  }

  const shipping = total >= 10000 ? 0 : 499;

  return (
    <main className="bg-[#FAF7F2] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="font-serif text-3xl md:text-4xl font-bold text-[#1F2A44]">Your Cart</h1>
        <p className="text-sm text-[#1F2A44]/70 mt-1">{items.length} item(s) in your cart</p>
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-20 grid lg:grid-cols-[1fr_360px] gap-8">
        {/* Items */}
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.cartKey || it.id} className="bg-white border border-[#1F2A44]/10 p-4 flex gap-4">
              <Link to={`/product/${it.productId || it.id}`} className="flex-shrink-0 w-24 h-24 md:w-28 md:h-28 bg-[#F3EFE8] overflow-hidden">
                <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/product/${it.productId || it.id}`} className="text-sm md:text-base text-[#1F2A44] font-medium hover:text-[#E8B530] line-clamp-2">{it.name}</Link>
                <p className="text-xs text-[#1F2A44]/60 mt-1 capitalize">{(it.category || '').replace('-', ' ')}</p>
                {it.variantLabel && <p className="text-xs text-[#1F2A44]/70 mt-1">Selected: {it.variantLabel}</p>}
                <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center border border-[#1F2A44]/20">
                    <button onClick={() => updateQty(it.cartKey || it.id, it.qty - 1)} className="p-1.5 hover:bg-[#F3EFE8] text-[#1F2A44]"><Minus size={12} /></button>
                    <span className="w-8 text-center text-sm text-[#1F2A44] font-semibold">{it.qty}</span>
                    <button onClick={() => updateQty(it.cartKey || it.id, it.qty + 1)} className="p-1.5 hover:bg-[#F3EFE8] text-[#1F2A44]"><Plus size={12} /></button>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="text-[#1F2A44] font-bold">{fmt(it.price * it.qty)}</p>
                    <button onClick={() => removeFromCart(it.cartKey || it.id)} className="text-[#1F2A44]/60 hover:text-red-600 transition-colors" aria-label="remove"><Trash2 size={16} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button onClick={clearCart} className="text-sm text-[#1F2A44]/60 hover:text-red-600 transition-colors">Clear cart</button>
        </div>

        {/* Summary */}
        <aside className="bg-white border border-[#1F2A44]/10 p-6 h-fit lg:sticky lg:top-32">
          <h2 className="font-serif text-xl font-bold text-[#1F2A44]">Order Summary</h2>
          <span className="inline-block mt-2 h-[2px] w-12 bg-[#E8B530]"></span>

          <div className="mt-5 space-y-3 text-sm">
            <div className="flex justify-between text-[#1F2A44]"><span>Subtotal</span><span className="font-semibold">{fmt(total)}</span></div>
            <div className="flex justify-between text-[#1F2A44]"><span>Shipping</span><span className="font-semibold">{shipping === 0 ? 'FREE' : fmt(shipping)}</span></div>
            <div className="flex justify-between text-[#1F2A44]"><span>Tax</span><span className="font-semibold">Included</span></div>
          </div>
          <div className="border-t border-[#1F2A44]/10 mt-4 pt-4 flex justify-between text-lg">
            <span className="font-semibold text-[#1F2A44]">Total</span>
            <span className="font-bold text-[#1F2A44]">{fmt(total + shipping)}</span>
          </div>

          <Link
            to="/checkout"
            className="flex w-full mt-6 items-center justify-center bg-[#E8B530] hover:bg-[#D4A220] text-[#1F2A44] font-semibold py-3.5 px-4 uppercase tracking-wider text-sm transition-colors"
          >Checkout</Link>
          <Link to="/shop" className="block text-center mt-3 text-sm text-[#1F2A44] hover:text-[#E8B530] transition-colors">Continue Shopping</Link>

          <p className="text-xs text-[#1F2A44]/60 mt-4 text-center">Free shipping on orders over {fmt(10000)}</p>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
